import type { CSSProperties } from "react";

const PURPLE = "var(--primary, #610cff)";
const PURPLE_TINT = "rgba(97, 12, 255, 0.05)"; // --primary at 5%
const GREEN = "#22c55e"; // Tailwind green-500 (no CSS-var token for inline use)
const GREEN_TINT = "rgba(34, 197, 94, 0.05)"; // green-500 at 5%
const SELECTION = "var(--primary, #610CFF)";
// Formula-edit highlight perimeters carry an EXPLICIT width so they never
// inherit the cell's own per-side border width (which per-cell borders can zero
// out or thicken). Matches the fixed 1px selection outline for a consistent
// affordance regardless of the underlying borders.
const HIGHLIGHT_BORDER_WIDTH = "1px";

export interface CalcCellStyleFlags {
  // A cell is visually "in the selection" (drawn by the fixed outline).
  isInSelection: boolean;
  // Formula-reference (blue dashed) perimeter edges.
  formulaCrossingTop?: boolean;
  formulaCrossingBottom?: boolean;
  formulaCrossingLeft?: boolean;
  formulaCrossingRight?: boolean;
  // Calc/edit-target (green) perimeter edges.
  formulaCrossingGreenTop?: boolean;
  formulaCrossingGreenBottom?: boolean;
  formulaCrossingGreenLeft?: boolean;
  formulaCrossingGreenRight?: boolean;
  isCalc: boolean;
  isCalcFocused: boolean;
  isFormulaEditing: boolean;
  isFormulaReferenced?: boolean;
  // When true, a contiguous-selection overlay component is drawing the
  // primary selection outline; suppress the per-cell primary outline so it
  // isn't doubled (calc/formula-edit outlines are unaffected).
  selectionOverlayActive?: boolean;
}

/**
 * Layer the calc / formula-edit background tints onto a cell's base styles.
 * The perimeter affordances (dashed formula-reference border, selection/calc
 * outline) live in computeCalcCellHighlightStyles so they can render ABOVE
 * the per-cell border overlay. All inputs are absent in the reader (no
 * selection / formula edit), so this collapses to `tdStyles`.
 */
export const computeCalcCellTdStyles = (
  tdStyles: CSSProperties,
  flags: CalcCellStyleFlags,
): CSSProperties => {
  // backgroundColor longhand (shorthand conflicts with backgroundClip).
  return flags.isFormulaEditing
    ? {
        ...tdStyles,
        backgroundColor: GREEN_TINT,
      }
    : flags.isFormulaReferenced
      ? {
          ...tdStyles,
          backgroundColor: PURPLE_TINT,
        }
      : !flags.isCalc
        ? tdStyles
        : flags.isCalcFocused
          ? {
              ...tdStyles,
              backgroundColor: GREEN_TINT,
            }
          : tdStyles;
};

/**
 * Perimeter affordances for a dedicated overlay that stacks ABOVE the
 * per-cell border overlay: the cell borders straddle the same edge geometry,
 * so drawing these on the td (which paints below positioned children) let a
 * thick border cover the 1px highlight. Returns null when nothing to draw.
 *
 * - Formula-reference (blue dashed) perimeter: explicit 1px per crossing side
 *   so it never inherits the cell's own per-side border width.
 * - Fixed 1px `outline`: green for a calc cell or the cell whose formula is
 *   being authored (isFormulaEditing also covers a plain cell not yet a calc
 *   cell), primary for a normal selection — NOT td borders or recolored
 *   neighbour edges: per-cell borders suppress a cell's own top/left edge when
 *   a neighbour owns the shared line, which made a border-based highlight land
 *   at the wrong width or only on some sides. `outline-offset: -0.5px` centres
 *   the stroke ON the cell's boundary so it sits on the grid line.
 */
export const computeCalcCellHighlightStyles = (
  flags: CalcCellStyleFlags,
): CSSProperties | null => {
  const formulaPerimeterStyles =
    flags.formulaCrossingTop ||
    flags.formulaCrossingBottom ||
    flags.formulaCrossingLeft ||
    flags.formulaCrossingRight
      ? {
          ...(flags.formulaCrossingTop && {
            borderTopColor: PURPLE,
            borderTopStyle: "dashed" as const,
            borderTopWidth: HIGHLIGHT_BORDER_WIDTH,
          }),
          ...(flags.formulaCrossingBottom && {
            borderBottomColor: PURPLE,
            borderBottomStyle: "dashed" as const,
            borderBottomWidth: HIGHLIGHT_BORDER_WIDTH,
          }),
          ...(flags.formulaCrossingLeft && {
            borderLeftColor: PURPLE,
            borderLeftStyle: "dashed" as const,
            borderLeftWidth: HIGHLIGHT_BORDER_WIDTH,
          }),
          ...(flags.formulaCrossingRight && {
            borderRightColor: PURPLE,
            borderRightStyle: "dashed" as const,
            borderRightWidth: HIGHLIGHT_BORDER_WIDTH,
          }),
        }
      : null;

  const wantsOutline =
    (flags.isInSelection && !flags.selectionOverlayActive) ||
    flags.isCalcFocused ||
    flags.isFormulaEditing;
  const selectionColor = wantsOutline
    ? flags.isCalc || flags.isFormulaEditing
      ? GREEN
      : SELECTION
    : undefined;
  const outlineStyles = selectionColor
    ? {
        outline: `1px solid ${selectionColor}`,
        outlineOffset: "-0.5px",
      }
    : null;

  if (!formulaPerimeterStyles && !outlineStyles) return null;
  return { ...formulaPerimeterStyles, ...outlineStyles };
};
